import { cn } from "@/lib/utils"
import { Loader2, Check, AlertCircle } from "lucide-react"
import { useEffect, useState } from "react"

export type AutoSaveStatus = "idle" | "saving" | "saved" | "error"

export interface AutoSaveIndicatorProps {
  /** Current auto-save status */
  status: AutoSaveStatus
  /** Number of consecutive auto-save failures (shown if > 0 in error state) */
  errorCount?: number
  /** Additional CSS classes */
  className?: string
}

/**
 * Visual indicator for auto-save status
 *
 * Shows:
 * - 🔄 "Saving draft..." (with spinner) when saving
 * - ✅ "Draft saved" (auto-hides after 2s) when saved
 * - ⚠️ "Auto-save failed (N times)" when error
 *
 * @example
 * ```tsx
 * const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>("idle")
 * const [errorCount, setErrorCount] = useState(0)
 *
 * <AutoSaveIndicator
 *   status={autoSaveStatus}
 *   errorCount={errorCount}
 * />
 * ```
 */
export function AutoSaveIndicator({
  status,
  errorCount = 0,
  className,
}: AutoSaveIndicatorProps) {
  const [isVisible, setIsVisible] = useState(false)

  // Show indicator when status changes, hide "saved" after 2s
  useEffect(() => {
    if (status === "idle") {
      setIsVisible(false)
      return
    }

    setIsVisible(true)

    if (status === "saved") {
      const timer = setTimeout(() => {
        setIsVisible(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [status])

  if (!isVisible) {
    return null
  }

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-all",
        status === "saving" && "bg-blue-50 text-blue-700",
        status === "saved" && "bg-green-50 text-green-700",
        status === "error" && "bg-red-50 text-red-700",
        className
      )}
      role="status"
      aria-live="polite"
    >
      {status === "saving" && (
        <>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span>Saving draft...</span>
        </>
      )}

      {status === "saved" && (
        <>
          <Check className="h-4 w-4" aria-hidden="true" />
          <span>Draft saved</span>
        </>
      )}

      {status === "error" && (
        <>
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <span>
            Auto-save failed
            {errorCount > 1 && ` (${errorCount} times)`}
          </span>
        </>
      )}
    </div>
  )
}
